import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  LinearProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import { useMsal } from '@azure/msal-react';
import { Client } from '@microsoft/microsoft-graph-client';
import { Application } from '@microsoft/microsoft-graph-types';
import { useAppCache } from '../context/AppCacheContext';

interface OwnershipAssignmentToolProps {
  onCancel: () => void;
  onSuccess?: () => void;
}

interface UserSearchResult {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail?: string;
}

interface AssignmentResult {
  appId: string;
  appName: string;
  success: boolean;
  error?: string;
}

const OwnershipAssignmentTool: React.FC<OwnershipAssignmentToolProps> = ({ onCancel, onSuccess }) => {
  const { instance, accounts } = useMsal();
  const { applications, appOwners, currentUserId } = useAppCache();
  
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // User selection states
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedOwners, setSelectedOwners] = useState<UserSearchResult[]>([]);
  
  // Assignment progress
  const [assigning, setAssigning] = useState(false);
  const [assignmentResults, setAssignmentResults] = useState<AssignmentResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Filter apps without owners
  const appsWithoutOwners = applications.filter(app => {
    const ownerInfo = appOwners[app.appId || app.id || ''];
    return !ownerInfo || ownerInfo.owners.length === 0;
  });

  const filteredApps = appsWithoutOwners.filter(app =>
    app.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.appId?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedApps(new Set(filteredApps.map(app => app.id || app.appId || '')));
    } else {
      setSelectedApps(new Set());
    }
  };

  const handleSelectApp = (appId: string) => {
    const newSelected = new Set(selectedApps);
    if (newSelected.has(appId)) {
      newSelected.delete(appId);
    } else {
      newSelected.add(appId);
    }
    setSelectedApps(newSelected);
  };

  const searchUsers = async (query: string) => {
    if (!query || query.length < 2) {
      setUserSearchResults([]);
      return;
    }

    setUserSearchLoading(true);
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['Directory.Read.All'],
        account: accounts[0]
      });

      const client = Client.init({
        authProvider: (done) => done(null, response.accessToken)
      });

      // Try $search first (better partial matching)
      try {
        const results = await client
          .api('/users')
          .search(`"${query}"`)
          .select('id,displayName,userPrincipalName,mail')
          .top(10)
          .header('ConsistencyLevel', 'eventual')
          .get();

        setUserSearchResults(results.value || []);
      } catch (searchErr: any) {
        // Fallback to filter-based search if $search fails
        console.log('$search failed, falling back to filter:', searchErr);
        const results = await client
          .api('/users')
          .filter(
            `startsWith(displayName,'${query}') or startsWith(userPrincipalName,'${query}') or startsWith(mail,'${query}')`
          )
          .select('id,displayName,userPrincipalName,mail')
          .top(10)
          .get();

        setUserSearchResults(results.value || []);
      }
    } catch (err: any) {
      console.error('Error searching users:', err);
      if (err.errorCode === 'consent_required' || err.errorCode === 'interaction_required') {
        setError('Admin consent required for Directory.Read.All permission. Please contact your administrator or use the Consent Manager tool.');
      } else {
        setError(`Failed to search users: ${err.message}`);
      }
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleAddOwner = (user: UserSearchResult) => {
    if (!selectedOwners.find(o => o.id === user.id)) {
      setSelectedOwners([...selectedOwners, user]);
    }
    setUserSearchOpen(false);
    setUserSearchQuery('');
    setUserSearchResults([]);
  };

  const handleRemoveOwner = (userId: string) => {
    setSelectedOwners(selectedOwners.filter(o => o.id !== userId));
  };

  const handleAssignOwners = async () => {
    console.log('🔧 Assignment started');
    console.log('📊 Selected apps count:', selectedApps.size);
    console.log('👥 Selected owners count:', selectedOwners.length);
    
    if (selectedApps.size === 0) {
      setError('Please select at least one application');
      return;
    }

    if (selectedOwners.length === 0) {
      setError('Please select at least one owner to assign');
      return;
    }

    setAssigning(true);
    setError(null);
    const results: AssignmentResult[] = [];

    try {
      console.log('🔑 Acquiring token...');
      const response = await instance.acquireTokenSilent({
        scopes: ['Application.ReadWrite.All'],
        account: accounts[0]
      });

      const client = Client.init({
        authProvider: (done) => done(null, response.accessToken)
      });

      console.log('✅ Token acquired, starting assignment...');

      // Assign owners to each selected app
      for (const appId of Array.from(selectedApps)) {
        const app = applications.find(a => a.id === appId || a.appId === appId);
        if (!app || !app.id) {
          console.warn('⚠️ App not found or missing ID:', appId);
          continue;
        }

        console.log(`📝 Assigning owners to app: ${app.displayName} (${app.id})`);

        try {
          // Add each selected owner to the app
          for (const owner of selectedOwners) {
            console.log(`  👤 Adding owner: ${owner.displayName} (${owner.id})`);
            const response = await client
              .api(`/applications/${app.id}/owners/$ref`)
              .post({
                '@odata.id': `https://graph.microsoft.com/v1.0/directoryObjects/${owner.id}`
              });
            console.log('  ✅ Owner added successfully', response);
          }

          console.log(`✅ All owners assigned successfully to ${app.displayName}`);
          results.push({
            appId: app.id,
            appName: app.displayName || 'Unknown',
            success: true
          });
        } catch (err: any) {
          console.error(`❌ Error assigning owners to app ${app.displayName}:`, err);
          console.error('Error details:', err);
          results.push({
            appId: app.id,
            appName: app.displayName || 'Unknown',
            success: false,
            error: err.message || 'Unknown error'
          });
        }
      }

      console.log('📊 Assignment complete. Results:', results);
      setAssignmentResults(results);
      setShowResults(true);

      // Invalidate both applications and owners cache to refresh data
      const currentTenantId = accounts[0]?.tenantId;
      if (currentTenantId && onSuccess) {
        setTimeout(() => {
          const appsTimestampKey = `appconfig_applications_timestamp_${currentTenantId}`;
          const ownersTimestampKey = `appconfig_owners_timestamp_${currentTenantId}`;
          localStorage.removeItem(appsTimestampKey);
          localStorage.removeItem(ownersTimestampKey);
          console.log("🔄 Ownership Assignment: Applications and owners cache invalidated");
          onSuccess();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error during ownership assignment:', err);
      setError(`Failed to assign owners: ${err.message}`);
    } finally {
      setAssigning(false);
    }
  };

  const handleCloseResults = () => {
    setShowResults(false);
    setSelectedApps(new Set());
    setSelectedOwners([]);
    setAssignmentResults([]);
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (userSearchQuery) {
        searchUsers(userSearchQuery);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [userSearchQuery]);

  // Results Dialog
  if (showResults) {
    const successCount = assignmentResults.filter(r => r.success).length;
    const failureCount = assignmentResults.filter(r => !r.success).length;

    return (
      <Box sx={{ p: 4 }}>
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              backgroundColor: successCount > 0 ? '#f0fdf4' : '#fef2f2',
              borderBottom: '1px solid rgba(0,0,0,0.12)'
            }}
          >
            <Box
              sx={{
                mr: 2,
                bgcolor: successCount > 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {successCount > 0 ? (
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.75rem' }} />
              ) : (
                <ErrorIcon sx={{ color: '#f44336', fontSize: '1.75rem' }} />
              )}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Ownership Assignment Complete
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {successCount} successful, {failureCount} failed
              </Typography>
            </Box>
          </Box>
        </Card>

        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Assignment Results
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Application</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignmentResults.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {result.success ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Success"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<ErrorIcon />}
                          label="Failed"
                          size="small"
                          color="error"
                        />
                      )}
                    </TableCell>
                    <TableCell>{result.appName}</TableCell>
                    <TableCell>
                      {result.success ? (
                        <Typography variant="body2" color="success.main">
                          {selectedOwners.length} owner(s) assigned
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="error">
                          {result.error}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleCloseResults}
            sx={{ borderRadius: 2 }}
          >
            Assign More Owners
          </Button>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ borderRadius: 2 }}
          >
            Back to Actions
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f3e8ff',
            borderBottom: '1px solid rgba(124, 58, 237, 0.2)'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>

          <Box
            sx={{
              borderLeft: '4px solid #7c3aed',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#7c3aed',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              App Ownership Assignment
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Bulk assign owners to applications lacking ownership
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: '#f44336' }}>
                {appsWithoutOwners.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Apps Without Owners
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: '#7c3aed' }}>
                {selectedApps.size}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Selected for Assignment
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: '#2196f3' }}>
                {selectedOwners.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Owners to Assign
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Selected Owners Section */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Owners to Assign
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setUserSearchOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Add Owner
            </Button>
          </Box>

          {selectedOwners.length === 0 ? (
            <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: 2 }}>
              No owners selected. Click "Add Owner" to search for users to assign as owners.
            </Alert>
          ) : (
            <List>
              {selectedOwners.map((owner, index) => (
                <React.Fragment key={owner.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleRemoveOwner(owner.id)}>
                        <ClearIcon />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#7c3aed' }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={owner.displayName}
                      secondary={owner.userPrincipalName || owner.mail}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}

          {selectedOwners.length > 0 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleAssignOwners}
                disabled={selectedApps.size === 0 || assigning}
                sx={{ borderRadius: 2 }}
              >
                {assigning ? 'Assigning...' : `Assign to ${selectedApps.size} App${selectedApps.size !== 1 ? 's' : ''}`}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Applications Selection */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Select Applications
          </Typography>

          <TextField
            label="Search Applications"
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by app name or App ID..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery('')} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          {filteredApps.length === 0 ? (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: 2 }}>
              All applications have assigned owners! No action needed.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedApps.size > 0 && selectedApps.size < filteredApps.length}
                          checked={filteredApps.length > 0 && selectedApps.size === filteredApps.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Application Name</TableCell>
                      <TableCell>Application ID</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredApps.map((app) => {
                      const appId = app.id || app.appId || '';
                      return (
                        <TableRow
                          key={appId}
                          hover
                          onClick={() => handleSelectApp(appId)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={selectedApps.has(appId)} />
                          </TableCell>
                          <TableCell>{app.displayName || 'Unknown'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {app.appId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<GroupIcon />}
                              label="No Owners"
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedApps.size} of {filteredApps.length} applications selected
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleAssignOwners}
                  disabled={selectedApps.size === 0 || selectedOwners.length === 0 || assigning}
                  sx={{ borderRadius: 2 }}
                >
                  {assigning ? 'Assigning...' : `Assign to ${selectedApps.size} App${selectedApps.size !== 1 ? 's' : ''}`}
                </Button>
              </Box>

              {assigning && <LinearProgress sx={{ mt: 2 }} />}
            </>
          )}
        </CardContent>
      </Card>

      {/* User Search Dialog */}
      <Dialog
        open={userSearchOpen}
        onClose={() => setUserSearchOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Search for Owner</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Search Users"
            variant="outlined"
            fullWidth
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            placeholder="Type name or email..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mt: 2, mb: 2 }}
          />

          {userSearchLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!userSearchLoading && userSearchResults.length === 0 && userSearchQuery.length >= 2 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No users found matching "{userSearchQuery}"
            </Alert>
          )}

          {userSearchResults.length > 0 && (
            <List>
              {userSearchResults.map((user) => (
                <ListItemButton
                  key={user.id}
                  onClick={() => handleAddOwner(user)}
                  disabled={selectedOwners.some(o => o.id === user.id)}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#2196f3' }}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.displayName}
                    secondary={user.userPrincipalName || user.mail}
                  />
                  {selectedOwners.some(o => o.id === user.id) && (
                    <Chip label="Already added" size="small" color="success" />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserSearchOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OwnershipAssignmentTool;
